import * as React from 'react';

/**
 * A short confetti burst for genuine milestones — a booking request sent, a
 * profile approved. Not for every save.
 *
 * Hand-rolled rather than pulling a confetti dependency: this is ~1KB of
 * canvas, where the smallest package is ~10KB gzipped for a single moment
 * in the app.
 *
 * Two things it deliberately does:
 *
 * 1. Honours `prefers-reduced-motion` by rendering nothing at all. Confetti
 *    is exactly the particle motion that triggers vestibular symptoms, and
 *    the media query is read at mount rather than baked at build time.
 * 2. Sits behind `aria-hidden` in a `pointer-events-none` layer, so it can
 *    never intercept a tap or reach a screen reader — the success message
 *    itself carries the meaning.
 */
export function SuccessBurst({
  /** Fires once when this flips true. */
  active = true,
  /** Particle count. Kept low — a burst, not a parade. */
  count = 44,
}: {
  active?: boolean;
  count?: number;
}) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [allowed, setAllowed] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setAllowed(!mq.matches);
    const onChange = () => setAllowed(!mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  React.useEffect(() => {
    if (!active || !allowed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Brand only — clay, teal, amber, ink. A rainbow here would undo the
    // palette discipline the rest of the app follows.
    const colours = ['#BB4D25', '#12735C', '#F2A93B', '#0D3A31'];
    const originX = width / 2;
    const originY = height * 0.38;

    const parts = Array.from({ length: count }, () => {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
      const speed = 3 + Math.random() * 5;
      return {
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 4 + Math.random() * 5,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        colour: colours[Math.floor(Math.random() * colours.length)],
      };
    });

    let raf = 0;
    const start = performance.now();
    const LIFE = 1400;

    const frame = (now: number) => {
      const elapsed = now - start;
      const fade = Math.max(0, 1 - elapsed / LIFE);
      ctx.clearRect(0, 0, width, height);

      for (const p of parts) {
        p.vy += 0.16; // gravity
        p.vx *= 0.99; // drag
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;

        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.colour;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }

      if (elapsed < LIFE) {
        raf = requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [active, allowed, count]);

  if (!allowed) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}
